import React from "react";

import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { trackPageView } from "@src/utils/analytics";
import { load as loadChatwoot } from "@src/utils/chatwoot";
import ChatwootLauncher from "@components/ChatwootLauncher";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "moment/locale/ru";
import "moment/locale/es";
import "moment/locale/en-gb";
import "moment/locale/fr";
import "moment/locale/de";
import "moment/locale/pl";
import "moment/locale/uk";
import "moment/locale/ro";
import "moment/locale/hu";

import { ROUTES } from "@stores/AccountStore";
import { ReactComponent as CloseIcon } from "@assets/icons/close.svg";

import CropsScreen from "@screens/CropsScreen";
import DeviceCalibrationScreen from "@screens/DeviceCalibrationScreen";
import DeviceTechnicalScreen from "@screens/DeviceTechnicalScreen/Screen";
import DevicesScreen from "@screens/DevicesScreen";
import FieldsScreen from "@screens/FieldsScreen";
import LoginScreen from "@screens/LoginScreen";
import RegisterScreen from "@screens/RegisterScreen";
import VerifyEmailScreen from "@screens/VerifyEmailScreen";
import PasswordForgotScreen from "@screens/PasswordForgotScreen";
import PasswordResetScreen from "@screens/PasswordResetScreen";
import OAuthConsentScreen from "@screens/OAuthConsentScreen";
import PrivateRoute from "@components/PrivateRoute";
import AdminRoute from "@components/AdminRoute";
import AdminScreen from "@screens/AdminScreen/AdminScreen";
import ProfileScreen from "@screens/ProfileScreen";
import SettingsScreen from "@screens/SettingsScreen";
import TracksScreen from "@screens/TracksScreen";
import PrescriptionScreen from "@screens/PrescriptionScreen";
import AgentScreen from "@screens/AgentScreen";
import AnalyticsScreen from "@screens/AnalyticsScreen";
import ReportsScreen from "@screens/ReportsScreen";
import TeamScreen from "@screens/TeamScreen";
import ReportView from "@screens/ReportView";
import JohnDeereCallbackHandler from "@components/JohnDeereCallbackHandler";
import NotificationBanner from "@components/NotificationBanner/NotificationBanner";


const Root = styled.div`
  width: 100%;
  min-height: 100vh;
`;

// Fires a PostHog pageview on every client-side route change.
const PageViewTracker: React.FC = () => {
  const location = useLocation();
  React.useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
};

const App: React.FC = () => {
  loadChatwoot();

  return (
    <Root>
      <PageViewTracker />
      <JohnDeereCallbackHandler />
      <NotificationBanner />
      <Routes>
        <Route path={ROUTES.LOGIN} element={<LoginScreen />} />
        <Route path={ROUTES.REGISTER} element={<RegisterScreen />} />
        <Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmailScreen />} />
        <Route path={ROUTES.PASSWORD_FORGOT} element={<PasswordForgotScreen />} />
        <Route path={ROUTES.PASSWORD_RESET} element={<PasswordResetScreen />} />
        <Route path={ROUTES.OAUTH_CONSENT} element={<OAuthConsentScreen />} />

        <Route
          path={ROUTES.PROFILE}
          element={
            <PrivateRoute route={ROUTES.PROFILE}>
              <ProfileScreen />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.TEAM}
          element={
            <PrivateRoute route={ROUTES.TEAM}>
              <TeamScreen />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.SETTINGS}
          element={
            <PrivateRoute route={ROUTES.SETTINGS}>
              <SettingsScreen />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.FIELDS}
          element={
            <PrivateRoute route={ROUTES.FIELDS}>
              <FieldsScreen />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.DEVICES}
          element={
            <PrivateRoute route={ROUTES.DEVICES}>
              <DevicesScreen />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.TRACKS}
          element={
            <PrivateRoute route={ROUTES.TRACKS}>
              <TracksScreen />
            </PrivateRoute>
          }
        />
        {/* Opened in its own tab from the Devices list, so it renders WITHOUT
            <Layout> — no LeftBar, no MobileTabBar — the same chrome-less shape
            as the shared report view. */}
        <Route
          path={ROUTES.DEVICE_TECHNICAL}
          element={
            <PrivateRoute route={ROUTES.DEVICE_TECHNICAL}>
              <DeviceTechnicalScreen />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.DEVICES_CALIBRATION}
          element={
            <PrivateRoute route={ROUTES.DEVICES_CALIBRATION}>
              <DeviceCalibrationScreen />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.CROPS}
          element={
            <PrivateRoute route={ROUTES.CROPS}>
              <CropsScreen />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.AGENT}
          element={
            <PrivateRoute route={ROUTES.AGENT}>
              <AgentScreen />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.ANALYTICS}
          element={
            <PrivateRoute route={ROUTES.ANALYTICS}>
              <AnalyticsScreen />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.REPORT_VIEW}
          element={
            <PrivateRoute route={ROUTES.REPORT_VIEW}>
              <ReportView />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.REPORTS}
          element={
            <PrivateRoute route={ROUTES.REPORTS}>
              <ReportsScreen />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.PRESCRIPTION}
          element={
            <PrivateRoute route={ROUTES.PRESCRIPTION}>
              <PrescriptionScreen />
            </PrivateRoute>
          }
        />
        <Route
          path={`${ROUTES.ADMIN}/*`}
          element={
            <AdminRoute>
              <AdminScreen />
            </AdminRoute>
          }
        />
        <Route path="*" element={<Navigate to={ROUTES.LOGIN} />} />
      </Routes>
      <ToastContainer
        closeButton={<CloseIcon style={{ width: 20, height: 20 }} />}
      />
      <ChatwootLauncher />
    </Root>
  );
};

export default observer(App);
